import { useEffect, useMemo, useRef, useState } from "react";

import type { Id } from "../../../api/types";
import { Banner, Button, Loading } from "../../../ui";
import { mountGraph, type GraphHandle } from "../graph/adapter";
import { buildElements, readPalette, type Palette } from "../graph/elements";
import { layoutForest } from "../graph/layout";
import { motionIsOn } from "../hooks";
import type { Highlight } from "../model/filters";
import { hueIndexes, type NavModel } from "../model/nav";

/** The colours of the theme, read again when the light or dark theme changes. */
function usePalette(): Palette {
  const [palette, setPalette] = useState<Palette>(() => readPalette());
  useEffect(() => {
    const update = () => setPalette(readPalette());
    const query = typeof window.matchMedia === "function" ? window.matchMedia("(prefers-color-scheme: dark)") : null;
    query?.addEventListener?.("change", update);
    const observer = typeof MutationObserver === "undefined" ? null : new MutationObserver(update);
    observer?.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => {
      query?.removeEventListener?.("change", update);
      observer?.disconnect();
    };
  }, []);
  return palette;
}

interface NavigatorGraphProps {
  model: NavModel;
  expanded: ReadonlySet<Id>;
  highlight: Highlight | null;
  selectedId: Id | null;
  onSelect(id: Id | null): void;
  onToggle(id: Id): void;
}

/**
 * The family tree drawn with Cytoscape.js, left to right, with a pie in each node for the progress and a ring in
 * the colour of its state. It is a picture: the list beside it holds the same tree for the keyboard and for screen
 * readers. Tap chooses a node, a double tap opens or folds it.
 */
export function NavigatorGraph({ model, expanded, highlight, selectedId, onSelect, onToggle }: NavigatorGraphProps) {
  const container = useRef<HTMLDivElement>(null);
  const handle = useRef<GraphHandle | null>(null);
  const handlers = useRef({ onSelect, onToggle });
  const lastTapped = useRef<Id | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "failed">("loading");
  const palette = usePalette();

  useEffect(() => {
    handlers.current = { onSelect, onToggle };
  });

  useEffect(() => {
    let cancelled = false;
    mountGraph(
      {
        container: container.current,
        palette: readPalette(),
        onSelect: (id) => {
          lastTapped.current = id;
          handlers.current.onSelect(id);
        },
        onToggle: (id) => handlers.current.onToggle(id),
      },
      () => cancelled,
    )
      .then((graph) => {
        if (!graph) return;
        if (cancelled) {
          graph.destroy();
          return;
        }
        handle.current = graph;
        setState("ready");
      })
      .catch(() => {
        if (!cancelled) setState("failed");
      });
    return () => {
      cancelled = true;
      handle.current?.destroy();
      handle.current = null;
    };
  }, []);

  const hues = useMemo(() => hueIndexes(model), [model]);
  const layout = useMemo(() => layoutForest(model, expanded), [model, expanded]);
  const elements = useMemo(
    () => buildElements({ model, expanded, layout, hues, highlight, selectedId, palette }),
    [model, expanded, layout, hues, highlight, selectedId, palette],
  );

  useEffect(() => {
    if (state === "ready") handle.current?.update(elements);
  }, [state, elements]);

  useEffect(() => {
    if (state === "ready") handle.current?.setPalette(palette);
  }, [state, palette]);

  // A choice made in the list brings the node to the middle. A tap on the picture leaves the picture where it is.
  useEffect(() => {
    if (state !== "ready" || !selectedId) return;
    if (lastTapped.current === selectedId) {
      lastTapped.current = null;
      return;
    }
    handle.current?.centerOn(selectedId, motionIsOn());
  }, [state, selectedId]);

  if (state === "failed") {
    return <Banner tone="warning">The map could not be drawn here. The list shows the same tree.</Banner>;
  }
  return (
    <div className="nv-graph">
      <div className="nv-graph__tools row" role="group" aria-label="Map controls">
        <Button variant="quiet" onClick={() => handle.current?.fit()}>
          Fit to screen
        </Button>
        <Button variant="quiet" onClick={() => handle.current?.zoomBy(1.4)}>
          Zoom in
        </Button>
        <Button variant="quiet" onClick={() => handle.current?.zoomBy(1 / 1.4)}>
          Zoom out
        </Button>
        <Button variant="quiet" disabled={!selectedId} onClick={() => selectedId && handle.current?.centerOn(selectedId, motionIsOn())}>
          Centre on the chosen one
        </Button>
        <span className="muted nv-graph__hint">Tap a name to choose it. Tap twice to open or fold it.</span>
      </div>
      <div ref={container} className="nv-graph__canvas" role="img" aria-label="Map of the family tree. The list holds the same tree." />
      {state === "loading" && <Loading>Drawing the map</Loading>}
    </div>
  );
}
